/* istanbul ignore file */
import { grid, maxWidthContainer, minBreakpoints } from '@noths/polaris-client-ribbons-base';
import { mediaQuery, pxToRem } from '@noths/polaris-client-styles';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { colors, spacing, typography } = themeVariables;
const spacingXxxxl = `calc(var(${spacing.xxl}) + var(${spacing.lg}))`;

export const wrapper = css`
  margin: 0 0 var(${spacing.xxxl}) 0;

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      margin: 0 0 ${spacingXxxxl} 0;
    `,
  )}
`;

export const container = css`
  ${grid}
  ${maxWidthContainer}
  background-color: color-mix(in srgb, var(${colors.surface.action}) 10%, var(${colors.surface
    .default}));
  grid-row-gap: 0;
`;

export const narrowContainer = css`
  ${mediaQuery(
    { max: minBreakpoints.M },
    css`
      margin: 0 var(${spacing.lg});
    `,
  )}
`;

export const content = css`
  text-align: center;
  grid-column: span 12;
  margin-top: calc(var(${spacing.md}) * -1);
  margin-bottom: var(${spacing.xl});

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      margin-top: 0;
      margin-bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      order: 1;
      grid-column: span 6;
    `,
  )}

  ${mediaQuery(
    { min: minBreakpoints.XL },
    css`
      grid-column: 2 / span 4;
      align-items: flex-start;
    `,
  )};
`;

export const indentedContent = css`
  ${mediaQuery(
    { min: minBreakpoints.XL },
    css`
      grid-column: 3 / span 4;
    `,
  )};
`;

export const darkTextContent = css`
  * {
    color: var(${colors.content.default});
  }
`;

export const imageWrapper = css`
  grid-column: span 12;

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      order: 2;
      grid-column: span 6;
    `,
  )};

  ${mediaQuery(
    { min: minBreakpoints.XL },
    css`
      grid-column: 7 / span 5;
    `,
  )};
`;

export const heroBannerImageWrapper = css`
  margin: var(${spacing.xl});
`;

export const heading = css`
  margin: var(${spacing.xl}) 0 0;
  font-family: var(${typography.heading.fontFamily});
  font-weight: var(${typography.heading.fontWeight});

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      margin: 0;
    `,
  )}
`;

export const subHeading = css`
  margin: var(${spacing.md}) 0 0;
  font-family: var(${typography.body.fontFamily});
  font-weight: var(${typography.body.fontWeight});
  font-size: var(${typography.body.sizes.lg.fontSize});
  line-height: var(${typography.body.sizes.lg.lineHeight});

  * {
    margin: 0;
    font-size: inherit;
    line-height: inherit;
  }
`;

export const copy = css`
  margin: var(${spacing.xl}) 0 0;

  & > * {
    margin: 0 0.5rem;
  }
`;

export const contentReverse = css`
  & div:nth-child(1) {
    ${mediaQuery(
      { min: minBreakpoints.M },
      css`
        order: 1;
      `,
    )};

    ${mediaQuery(
      { min: minBreakpoints.XL },
      css`
        grid-column: 2 / span 5;
      `,
    )};
  }
  & div:nth-child(2) {
    ${mediaQuery(
      { min: minBreakpoints.M },
      css`
        order: 2;
      `,
    )}

    ${mediaQuery(
      { min: minBreakpoints.XL },
      css`
        grid-column: 8 / span 4;
      `,
    )};
  }
`;

export const iconsBulletList = css`
  padding: 0;
  margin: 0;
  list-style: none;
  /* Mirrors <ol start>: first number is counter-reset + 1 after increment on each li */
  counter-reset: item var(--ol-start, 0);
`;

export const iconsBulletItem = css`
  display: flex;
  align-items: center;
  gap: var(${spacing.xs});
  text-align: left;
  counter-increment: item;

  &::before {
    content: counter(item);
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: ${pxToRem(28)};
    height: ${pxToRem(28)};
    border-radius: 50%;
    background-color: #51285f;
    color: var(${colors.surface.default}, #fff);
    font-family: var(${typography.heading.fontFamily});
    font-weight: var(${typography.heading.fontWeight});
    font-size: var(${typography.heading.sizes.md.fontSize});
    line-height: var(${typography.heading.sizes.md.lineHeight});
  }
`;

export const iconsBulletItemImg = css`
  width: 42px;
  height: 32px;
  flex: 0 0 42px;

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      width: 63px;
      height: 48px;
      flex: 0 0 63px;
    `,
  )};
`;

export const iconsBulletItemText = css`
  margin: var(${spacing.sm}) 0;
  font-family: var(${typography.body.fontFamily});
  font-weight: var(${typography.body.fontWeight});

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      max-width: 294px;
      margin: var(${spacing.md}) 0;
    `,
  )};
`;

export const qrCode = css`
  display: none;
  max-width: 240px;
  margin: 0 auto;
  padding-top: var(${spacing.xxxl});
  text-align: center;

  img {
    width: 176px;
    margin: 0 auto;
    border-radius: ${pxToRem(10)};
  }

  p {
    font-family: var(${typography.body.fontFamily});
    font-weight: var(${typography.body.fontWeight});
    font-size: var(${typography.body.sizes.lg.fontSize});
    line-height: var(${typography.body.sizes.lg.lineHeight});
  }

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      display: block;
    `,
  )};
`;
